export interface RouteStopDetail {
  order: number;
  name: string;
  type: string;
  duration: string;
  icon: string;
  description: string;
  venueId: string | null;
  address: string;
  tip: string;
}

export interface CityRouteDetail {
  id: string;
  title: string;
  description: string;
  longDescription: string;
  durationHours: number;
  difficulty: "easy" | "medium" | "hard";
  image: string;
  category: string;
  tags: string[];
  idealFor: string[];
  transportNote: string;
  totalDistanceKm: number;
  stops: RouteStopDetail[];
}

const STOP_ICONS: Record<string, string> = {
  "Tarihi": "ri-ancient-pavilion-line",
  "Kültür": "ri-gallery-line",
  "Yemek": "ri-restaurant-line",
  "Öğle": "ri-restaurant-line",
  "Akşam": "ri-restaurant-line",
  "Kahvaltı": "ri-cup-line",
  "Doğa": "ri-plant-line",
  "Eğlence": "ri-gamepad-line",
  "Macera": "ri-rocket-line",
  "Konaklama": "ri-hotel-line",
  "Atıştırma": "ri-cake-2-line",
  "Tatlı": "ri-cake-2-line",
  "Alışveriş": "ri-shopping-bag-line",
  "Kahve": "ri-cup-line",
  "Park": "ri-tree-line",
};

export function getStopIcon(type: string): string {
  return STOP_ICONS[type] ?? "ri-map-pin-line";
}

export const cityRouteDetails: CityRouteDetail[] = [
  {
    id: "one-day-bursa",
    title: "Bir Günde Bursa",
    description: "Şehrin en ikonik noktalarını tek günde keşfedin",
    longDescription: "Sabah erken başlayıp akşam termal sularda yorgunluğunuzu atmak için mükemmel bir 8 saatlik rota. Tarihin, lezzetin ve doğanın bir araya geldiği en yoğun Bursa deneyimi. Günü verimli geçirmek isteyenler için idealdir.",
    durationHours: 8,
    difficulty: "easy",
    image: "https://public.readdy.ai/ai/img_res/cdf72205e1bb0b98a9d3d2b52b773386.jpg",
    category: "Genel",
    tags: ["Tarih", "Lezzet", "Doğa", "Termal"],
    idealFor: ["İlk Ziyaretçiler", "Çiftler", "Grup Gezileri"],
    transportNote: "Araç veya taksi önerilir. Teleferik ve Extrempark arası minibüs var.",
    totalDistanceKm: 42,
    stops: [
      {
        order: 1, name: "Ulu Cami", type: "Tarihi", duration: "45 dk", icon: "ri-ancient-pavilion-line",
        description: "20 kubbe ve muhteşem hat yazılarıyla Osmanlı mimarisinin zirvesi. Sabah ışığında en güzel hali.",
        venueId: "ulu-cami", address: "Atatürk Cad., Osmangazi", tip: "Sabah 8-9 arası çok sakin, erken gidin",
      },
      {
        order: 2, name: "Hanlar Bölgesi", type: "Kültür", duration: "1 sa", icon: "ri-gallery-line",
        description: "Koza Han'ın avlusunda çay içip tarihi hanları keşfedin. Yadigar ve hediyeler için ideal.",
        venueId: "hanlar-bolgesi", address: "Kapalı Çarşı Çevresi, Osmangazi", tip: "Koza Han'da Türk kahvesi içmeyi unutmayın",
      },
      {
        order: 3, name: "Kebapçı İskender", type: "Yemek", duration: "1 sa", icon: "ri-restaurant-line",
        description: "1867'den bu yana aynı tarif. Bursa'da İskender yemeden dönmek olmaz.",
        venueId: "kebapci-iskender", address: "Atatürk Cad. 60, Osmangazi", tip: "Kalabalık olur, rezervasyon yapın",
      },
      {
        order: 4, name: "Yeşil Cami", type: "Tarihi", duration: "30 dk", icon: "ri-ancient-pavilion-line",
        description: "Türkuaz çinileriyle göz kamaştıran bu cami, fotoğraf çekimi için biçilmiş kaftan.",
        venueId: "yesil-cami", address: "Yeşil Mah., Yıldırım", tip: "Öğle namazına denk getirmeyin",
      },
      {
        order: 5, name: "Yeşil Türbe", type: "Tarihi", duration: "30 dk", icon: "ri-ancient-pavilion-line",
        description: "6 asırdır değişmeyen turkuaz çiniler. Sultan Mehmed I burada yatıyor.",
        venueId: "yesil-turbe", address: "Yeşil Mah., Yıldırım", tip: "Yeşil Cami'nin tam karşısında, birlikte gezin",
      },
      {
        order: 6, name: "Bursa Teleferik", type: "Doğa", duration: "1.5 sa", icon: "ri-plant-line",
        description: "Şehirden Uludağ'a panoramik yolculuk. Manzara inanılmaz, fotoğraflarınız harika çıkacak.",
        venueId: "bursa-teleferik", address: "Namazgah, Osmangazi", tip: "Hafta sonu kuyruğu uzun olabiliyor",
      },
      {
        order: 7, name: "Extrempark", type: "Eğlence", duration: "1.5 sa", icon: "ri-rocket-line",
        description: "Dağ kızağı ve dev salıncak ile adrenalin dolu kapanış. Uludağ eteklerinde macera.",
        venueId: "extrempark", address: "Uludağ Yolu, Osmangazi", tip: "Online bilet %15 daha ucuz",
      },
      {
        order: 8, name: "Çekirge Termal", type: "Konaklama", duration: "1 sa", icon: "ri-hotel-line",
        description: "Günü bitirmenin en güzel yolu: Mineral zengini termal sularda dinlenmek.",
        venueId: "gumusçpala-termal", address: "Çekirge, Osmangazi", tip: "Day-use (günlük kullanım) seçeneği sorabilirsiniz",
      },
    ],
  },
  {
    id: "historical-bursa",
    title: "Tarihi Bursa Rotası",
    description: "Osmanlı'nın izini süren kapsamlı tarih turu",
    longDescription: "Bursa, Osmanlı İmparatorluğu'nun ilk başkentidir ve bu rota size bu büyük tarihin derinliklerine inmek için kapsamlı bir rehber sunar. Türbelerden hanlar bölgesine, camilerden UNESCO köyüne uzanan tam bir tarih yolculuğu.",
    durationHours: 10,
    difficulty: "medium",
    image: "https://readdy.ai/api/search-image?query=Bursa%20Ottoman%20historical%20route%20mosque%20mausoleum%20stone%20architecture%20ancient%20bazaar%20covered%20market%20warm%20heritage%20panoramic%20Turkey%20travel%20guide&width=1200&height=600&seq=rdpage_route02&orientation=landscape",
    category: "Tarih",
    tags: ["Osmanlı", "UNESCO", "Mimari", "Müze"],
    idealFor: ["Tarih Severler", "Akademisyenler", "Kültür Turları"],
    transportNote: "Merkezdeki noktalar yürüme mesafesinde. Cumalıkızık için araç gerekli.",
    totalDistanceKm: 35,
    stops: [
      {
        order: 1, name: "Osman Gazi Türbesi", type: "Tarihi", duration: "30 dk", icon: "ri-ancient-pavilion-line",
        description: "Osmanlı'nın başladığı yer. Devletin kurucusu Osman Gazi'nin türbesi.",
        venueId: "osman-gazi-turbesi", address: "Tophane, Osmangazi", tip: "Tophane Parkı'ndan şehir manzarası harika",
      },
      {
        order: 2, name: "Ulu Cami", type: "Tarihi", duration: "1 sa", icon: "ri-ancient-pavilion-line",
        description: "20 kubbesi ve 192 hat yazısıyla Osmanlı camilerinin başyapıtı.",
        venueId: "ulu-cami", address: "Atatürk Cad., Osmangazi", tip: "Şadırvana ve kubbe nakışlarına dikkat edin",
      },
      {
        order: 3, name: "Bursa Kapalı Çarşısı", type: "Alışveriş", duration: "1 sa", icon: "ri-shopping-bag-line",
        description: "14. yüzyıldan beri ayakta olan tarihi çarşı. İpekli kumaşlar buranın özüdür.",
        venueId: "kapali-carsi", address: "Osmangazi", tip: "Kestane şekeri ve havlu almayı unutmayın",
      },
      {
        order: 4, name: "Hanlar Bölgesi", type: "Kültür", duration: "1.5 sa", icon: "ri-gallery-line",
        description: "Koza Han, Fidan Han ve Pirinç Han. Osmanlı ticaret dünyasının kalıntıları.",
        venueId: "hanlar-bolgesi", address: "Kapalı Çarşı Çevresi, Osmangazi", tip: "Her hanın farklı bir hikayesi var",
      },
      {
        order: 5, name: "Nilüfer Hatun İmareti", type: "Tarihi", duration: "30 dk", icon: "ri-ancient-pavilion-line",
        description: "14. yüzyıldan kalma imaret, bugün Türk-İslam eserleri müzesi.",
        venueId: "nilüfer-imareti", address: "Osmangazi", tip: "Küçük ama etkileyici bir müze",
      },
      {
        order: 6, name: "Yeşil Cami", type: "Tarihi", duration: "45 dk", icon: "ri-ancient-pavilion-line",
        description: "Türk çini sanatının en güzel örneği. Yüzyıllardır solmayan türkuaz renkler.",
        venueId: "yesil-cami", address: "Yeşil Mah., Yıldırım", tip: "İçerideki süslemelere yakından bakın",
      },
      {
        order: 7, name: "Yeşil Türbe", type: "Tarihi", duration: "30 dk", icon: "ri-ancient-pavilion-line",
        description: "Sultan Mehmed I'in türbesi. Çini ustalarının şaheseri.",
        venueId: "yesil-turbe", address: "Yeşil Mah., Yıldırım", tip: "Yeşil Cami ile birlikte gezin",
      },
      {
        order: 8, name: "Cumalıkızık", type: "Kültür", duration: "2 sa", icon: "ri-gallery-line",
        description: "700 yıllık UNESCO Dünya Mirası köyü. Tarihin en özgün hali.",
        venueId: "cumalikizik-koy", address: "Yıldırım, Bursa", tip: "Dar sokaklarda yürümek ayrı bir deneyim",
      },
    ],
  },
  {
    id: "food-tour",
    title: "Lezzet Turu",
    description: "Bursa'nın en iyi tatlarını tüm gün boyunca keşfedin",
    longDescription: "Sabah köy kahvaltısından akşam pideli köfteye uzanan bir gastronomi yolculuğu. Bursa, Türk mutfağının başkentlerinden biridir ve bu rota sizi en özgün lezzetlerle buluşturuyor. Tok gelinmesi önerilmez!",
    durationHours: 6,
    difficulty: "easy",
    image: "https://readdy.ai/api/search-image?query=Turkish%20food%20tour%20Bursa%20gastronomy%20iskender%20kebab%20tasting%20authentic%20cuisine%20local%20market%20restaurant%20street%20food%20diverse%20spread%20warm%20appetizing%20table&width=1200&height=600&seq=rdpage_route03&orientation=landscape",
    category: "Lezzet",
    tags: ["Kahvaltı", "Kebap", "Pide", "Tatlı", "Kahve"],
    idealFor: ["Gurme Sevenler", "Foodie", "Çiftler"],
    transportNote: "Çoğu durak Osmangazi'de, yürüme mesafesinde. Cumalıkızık için araç lazım.",
    totalDistanceKm: 28,
    stops: [
      {
        order: 1, name: "Cumalıkızık Köy Sofrası", type: "Kahvaltı", duration: "1.5 sa", icon: "ri-cup-line",
        description: "700 yıllık köyde organik köy kahvaltısı. Bal, kaymak, köy yumurtası ve domates.",
        venueId: "cumalikizik-sofrasi", address: "Cumalıkızık, Yıldırım", tip: "Hafta sonu rezervasyon şart!",
      },
      {
        order: 2, name: "Kapalı Çarşı Tatları", type: "Atıştırma", duration: "45 dk", icon: "ri-cake-2-line",
        description: "Kestane şekeri, tahinli lokum ve kuruüzüm tatlıları. Çarşının gizli lezzetleri.",
        venueId: "kapali-carsi", address: "Osmangazi", tip: "Dükkanlarda tadım yapabilirsiniz",
      },
      {
        order: 3, name: "Kebapçı İskender", type: "Öğle", duration: "1 sa", icon: "ri-restaurant-line",
        description: "1867'de icat edilen orijinal İskender. Tereyağı ve domates sosuyla gelen dilimlenmiş kuzu eti.",
        venueId: "kebapci-iskender", address: "Atatürk Cad. 60, Osmangazi", tip: "Yanında ayran siparişi unutmayın",
      },
      {
        order: 4, name: "Eski Pideciler Çarşısı", type: "Atıştırma", duration: "30 dk", icon: "ri-cake-2-line",
        description: "Tahinli pide ve pideli köftenin en özgün adresi. Taş fırından çıkan pide bambaşka.",
        venueId: "pideciler-carsisi", address: "Kapalı Çarşı Yanı, Osmangazi", tip: "Tahinli pide muhakkak deneyin",
      },
      {
        order: 5, name: "Güneşli Pastanesi", type: "Tatlı", duration: "30 dk", icon: "ri-cake-2-line",
        description: "Bursa'nın geleneksel kestane şekeri tatlıları ve Türk kahvesi. Öğleden sonra molası.",
        venueId: null, address: "Heykel, Osmangazi", tip: "Kestane şekerli baklava mutlaka deneyin",
      },
      {
        order: 6, name: "Extrempark Terası", type: "Kahve", duration: "30 dk", icon: "ri-cup-line",
        description: "Uludağ manzarası eşliğinde Türk çayı. Günün en iyi fotoğrafı buradan çekilir.",
        venueId: "extrempark", address: "Uludağ Yolu, Osmangazi", tip: "Gün batımı saatlerine denk getirin",
      },
      {
        order: 7, name: "Pideciler Çarşısı Akşamı", type: "Akşam", duration: "1 sa", icon: "ri-restaurant-line",
        description: "Güne farklı bir pide çeşidi ile nokta koyun. Kıymalı pide akşam için ideal.",
        venueId: "pideciler-carsisi", address: "Kapalı Çarşı Yanı, Osmangazi", tip: "Akşamları daha az kalabalık",
      },
    ],
  },
  {
    id: "family-route",
    title: "Aile Rotası",
    description: "Çocuklar ve aileler için eğlenceli ve rahat bir Bursa turu",
    longDescription: "Her yaştan ziyaretçiye hitap eden, yorucu olmayan ve bolca eğlence içeren bir gün planı. Kültürel deneyim, doğa aktivitesi ve adrenalin bir arada. Çocuklar kesinlikle sevecek!",
    durationHours: 8,
    difficulty: "easy",
    image: "https://readdy.ai/api/search-image?query=family%20adventure%20Bursa%20Turkey%20children%20outdoor%20fun%20cable%20car%20nature%20park%20activities%20kids%20Uludag%20summer%20holiday%20forest%20green%20joyful&width=1200&height=600&seq=rdpage_route04&orientation=landscape",
    category: "Aile",
    tags: ["Çocuk Dostu", "Macera", "Doğa", "Eğlence"],
    idealFor: ["Aileler", "Çocuklu Gruplar", "Hafta Sonu"],
    transportNote: "Araç ile gidilmesi şiddetle tavsiye edilir. Park yeri bol.",
    totalDistanceKm: 38,
    stops: [
      {
        order: 1, name: "Cumalıkızık", type: "Kültür", duration: "1.5 sa", icon: "ri-gallery-line",
        description: "Çocuklar tarihi taş sokakları ve köy atmosferini bayılacak. Kendi kendine keşif zamanı.",
        venueId: "cumalikizik-koy", address: "Yıldırım, Bursa", tip: "Çocuk arabası için yollar dar, taşıyıcı öneririz",
      },
      {
        order: 2, name: "Bursa Botanik Parkı", type: "Park", duration: "1 sa", icon: "ri-tree-line",
        description: "Geniş yeşil alanlar, gölet ve oyun alanları. Çocukların koşup oynayabileceği güvenli alan.",
        venueId: "botanik-park", address: "Çekirge, Osmangazi", tip: "Piknik sepeti getirin!",
      },
      {
        order: 3, name: "Aile Öğle Yemeği", type: "Öğle", duration: "1 sa", icon: "ri-restaurant-line",
        description: "Çocuk menüsü olan aile restoranı. Herkesin seveceği Türk mutfağı lezzetleri.",
        venueId: "haci-bey", address: "Altıparmak Cad., Osmangazi", tip: "Hacı Bey geniş aile mekanıdır",
      },
      {
        order: 4, name: "Bursa Teleferik", type: "Eğlence", duration: "1.5 sa", icon: "ri-plant-line",
        description: "Hava içinde süzülerek Uludağ'a çıkış. Çocukların gözleri açılacak!",
        venueId: "bursa-teleferik", address: "Namazgah, Osmangazi", tip: "6 yaş altı çocuklar ücretsiz",
      },
      {
        order: 5, name: "Extrempark", type: "Macera", duration: "2 sa", icon: "ri-rocket-line",
        description: "Dağ kızağı, mini zipline ve çocuk aktivite alanı. Ebeveynler de keyif yapacak.",
        venueId: "extrempark", address: "Uludağ Yolu, Osmangazi", tip: "Boy ve yaş sınırlarını önceden kontrol edin",
      },
      {
        order: 6, name: "İskender ile Günü Kapat", type: "Akşam", duration: "1 sa", icon: "ri-restaurant-line",
        description: "Yorgun ama mutlu. İskender Kebap ile Bursa'da son öğün.",
        venueId: "kebapci-iskender", address: "Atatürk Cad. 60, Osmangazi", tip: "Çocuk porsiyon sorabilirsiniz",
      },
    ],
  },
  {
    id: "weekend-plan",
    title: "Hafta Sonu Planı",
    description: "2 günlük kapsamlı Bursa deneyimi, her şey dahil",
    longDescription: "Cumartesi sabahından Pazar akşamına kadar eksiksiz bir Bursa deneyimi. Tarihin derinlikleri, lezzetin doruk noktası, doğanın huzuru ve termal suların dinginliği. Şehri gerçekten tanımak isteyenler için.",
    durationHours: 18,
    difficulty: "medium",
    image: "https://readdy.ai/api/search-image?query=Bursa%20Turkey%20weekend%20travel%20guide%20mountain%20city%20panoramic%20view%20Uludag%20green%20nature%20historical%20mosques%20hotels%20spectacular%20scenery&width=1200&height=600&seq=rdpage_route05&orientation=landscape",
    category: "Hafta Sonu",
    tags: ["Kapsamlı", "2 Gün", "Her Şey Dahil", "Termal"],
    idealFor: ["Uzun Tatil", "Çiftler", "Detaylı Keşif"],
    transportNote: "Araç şart. 2 gün için termal otel rezervasyonu yapmanız önerilir.",
    totalDistanceKm: 85,
    stops: [
      {
        order: 1, name: "Ulu Cami", type: "Tarihi", duration: "45 dk", icon: "ri-ancient-pavilion-line",
        description: "CUMARTESİ: Güne Osmanlı mimarisinin zirvesiyle başlayın.",
        venueId: "ulu-cami", address: "Atatürk Cad., Osmangazi", tip: "Sabah erken — çok sakin oluyor",
      },
      {
        order: 2, name: "Hanlar Bölgesi & Alışveriş", type: "Alışveriş", duration: "1 sa", icon: "ri-shopping-bag-line",
        description: "Koza Han avlusunda çay, tarihi hanları keşfetme ve yerel alışveriş.",
        venueId: "hanlar-bolgesi", address: "Kapalı Çarşı Çevresi", tip: "İpek ürünleri en iyi hediyedir",
      },
      {
        order: 3, name: "İskender Kebap Öğlesi", type: "Öğle", duration: "1 sa", icon: "ri-restaurant-line",
        description: "Bursa'nın simge yemeği. Orijinal tarif, aynı aile.",
        venueId: "kebapci-iskender", address: "Atatürk Cad. 60, Osmangazi", tip: "Tatlı için kestane şekeri alın",
      },
      {
        order: 4, name: "Yeşil Cami & Türbe", type: "Tarihi", duration: "1 sa", icon: "ri-ancient-pavilion-line",
        description: "Öğleden sonra tarihi atmosfer. İki yapıyı birlikte gezmek 1 saate sığıyor.",
        venueId: "yesil-cami", address: "Yeşil Mah., Yıldırım", tip: "Yeşil Türbe ile birlikte planla",
      },
      {
        order: 5, name: "Cumalıkızık Akşamı", type: "Kültür", duration: "2 sa", icon: "ri-gallery-line",
        description: "Gün batımında UNESCO köyünü gezmek çok farklı. Sabah kalabalığı yok.",
        venueId: "cumalikizik-koy", address: "Yıldırım, Bursa", tip: "Köy içinde küçük kafeler var",
      },
      {
        order: 6, name: "Termal Otel Konaklaması", type: "Konaklama", duration: "1 Gece", icon: "ri-hotel-line",
        description: "Çekirge'de termal otel. Doğal mineral suları, hamam ve spa ile tam dinlenme.",
        venueId: "kervansaray-termal", address: "Çekirge, Osmangazi", tip: "Kervansaray veya Gümüşpala öneririz",
      },
      {
        order: 7, name: "Teleferik ile Uludağ", type: "Doğa", duration: "1.5 sa", icon: "ri-plant-line",
        description: "PAZAR: Sabah teleferik ile Uludağ'a çıkış. Bulutların üzerinde kahvaltı.",
        venueId: "bursa-teleferik", address: "Namazgah, Osmangazi", tip: "Sabah erken daha az kuyruk",
      },
      {
        order: 8, name: "Extrempark & Öğle", type: "Macera", duration: "3 sa", icon: "ri-rocket-line",
        description: "Dağ kızağı, zipline ve son bir macera. Sonra restoranda dinlenme.",
        venueId: "extrempark", address: "Uludağ Yolu, Osmangazi", tip: "Online bilet alarak sıraya girme",
      },
      {
        order: 9, name: "Cumalıkızık Kahvaltısı ile Dönüş", type: "Kahvaltı", duration: "1.5 sa", icon: "ri-cup-line",
        description: "Ya da evden önce Cumalıkızık'ta köy kahvaltısıyla güzel bir kapanış.",
        venueId: "cumalikizik-sofrasi", address: "Cumalıkızık, Yıldırım", tip: "Rezervasyonu önceden yapın",
      },
    ],
  },
];
